import { effect, inject, Injectable } from '@angular/core';
import type { IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import { ApiRow } from '../types/api-row';
import { SalesStatisticsQueryService } from './sales-statistics-query.service';

/** Key for one server-side block. Built in one place because the plain path and
    the pivot path both write it and `getRows` reads it — drifting between them
    silently disables the cache.

    `inventoryEnabled` is part of it: rows fetched with the Lagerwert toggle off
    carry no inventory values, and a page that is ordered by a Lagerwert column
    takes the aggregate inline, where the backend answers the value columns from
    its combined invoice + stock path. Keeping both states side by side also means
    toggling back and forth costs no query. */
export function gridCacheKey(
  request: Pick<
    IServerSideGetRowsRequest,
    | 'startRow'
    | 'endRow'
    | 'groupKeys'
    | 'rowGroupCols'
    | 'sortModel'
    | 'pivotMode'
    | 'pivotCols'
    | 'valueCols'
  >,
  inventoryEnabled: boolean
): string {
  return JSON.stringify({
    startRow: request.startRow ?? 0,
    endRow: request.endRow ?? 100,
    groupKeys: request.groupKeys,
    rowGroupColIds: request.rowGroupCols.map((c) => c.id),
    sortModel: request.sortModel,
    pivotMode: request.pivotMode,
    pivotColIds: request.pivotCols.map((c) => c.id),
    valueColIds: request.valueCols.map((c) => c.id),
    inventoryEnabled,
  });
}

export interface GridCacheEntry {
  rowData: ApiRow[];
  rowCount?: number;
  /** Dynamic pivot column keys (pivot mode only). */
  pivotResultFields?: string[];
}

@Injectable()
export class SalesStatisticsGridCacheService {
  private readonly queryService = inject(SalesStatisticsQueryService);
  private readonly entries = new Map<string, GridCacheEntry>();

  constructor() {
    let lastInvalidationKey: string | null = null;
    effect(() => {
      const key = JSON.stringify([
        this.queryService.activeFilters(),
        this.queryService.dateRange(),
        this.queryService.referencePeriod(),
        this.queryService.documentType(),
        this.queryService.statisticType(),
        this.queryService.refreshTick(),
      ]);
      if (lastInvalidationKey === null) {
        lastInvalidationKey = key;
        return;
      }
      if (lastInvalidationKey === key) return;
      lastInvalidationKey = key;
      this.entries.clear();
    });
  }

  get(key: string): GridCacheEntry | undefined {
    return this.entries.get(key);
  }

  set(key: string, entry: GridCacheEntry): void {
    this.entries.set(key, entry);
  }
}
